import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../i18n/LanguageContext';

const API_BASE_URL = (process.env as any).REACT_APP_API_URL || 'http://localhost:8000';

const DiceScreen = () => {
  const { t } = useLanguage();
  const userId = (window as any)?.Telegram?.WebApp?.initDataUnsafe?.user?.id?.toString() || 'demo';
  
  const [balance, setBalance] = useState<number>(0);
  const [betAmount, setBetAmount] = useState<string>('1');
  const [target, setTarget] = useState<number>(50);
  const [rollType, setRollType] = useState<'under' | 'over'>('under');
  const [rolling, setRolling] = useState<boolean>(false);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadBalance();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBalance = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/user/balance`, {
        params: { wallet_address: userId },
        timeout: 30000
      });
      setBalance(response.data.balance);
    } catch (err: any) {
      console.error('Error loading balance:', err);
    }
  };

  const calculateMultiplier = (): number => {
    const winChance = rollType === 'under' ? target / 100 : (100 - target) / 100;
    return winChance > 0 ? 0.98 / winChance : 0;
  };

  const calculateWinChance = (): number => {
    return rollType === 'under' ? target : 100 - target;
  };

  const handleRoll = async () => {
    setError('');
    setResult(null);

    const betNum = parseFloat(betAmount);
    if (isNaN(betNum) || betNum < 0.1) {
      setError(t.minBetError);
      return;
    }
    
    if (betNum > 10) {
      setError(t.maxBetError);
      return;
    }

    if (betNum > balance) {
      setError(t.insufficientFunds);
      return;
    }

    setRolling(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/games/dice/play`, {
        wallet_address: userId,
        bet_amount: betNum,
        target: target,
        roll_type: rollType
      }, { timeout: 30000 });

      // Анимация прокрутки
      await new Promise(resolve => setTimeout(resolve, 1500));

      setResult(response.data);
      setBalance(response.data.new_balance);
    } catch (err: any) {
      console.error('Error rolling dice:', err);
      setError(err.response?.data?.detail || t.gameError);
    } finally {
      setRolling(false);
    }
  };

  const multiplier = calculateMultiplier();
  const winChance = calculateWinChance();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-600 to-pink-600 pb-20 px-4 pt-6">
      {/* Header */}
      <div className="card bg-gradient-to-br from-purple-700/80 to-pink-800/80 text-white text-center mb-4">
        <div className="text-3xl font-bold mb-2">🎯 {t.game1to100}</div>
        <div className="text-sm opacity-90">{t.diceGuessNumber}</div>
        <div className="text-2xl font-bold mt-2">{balance.toFixed(2)} TON</div>
      </div>

      {/* Main Game Area */}
      <div className="card bg-white/90 mb-4">
        {/* Bet Amount */}
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">{t.diceBetAmount}</label>
          <input
            type="number"
            step="0.1"
            min="0.1"
            max="10"
            value={betAmount}
            onChange={(e) => setBetAmount(e.target.value)}
            disabled={rolling}
            className="w-full px-4 py-3 rounded-lg border-2 border-purple-300 focus:border-purple-500 outline-none text-lg font-bold"
          />
        </div>

        {/* Roll Type Selection */}
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">{t.diceChooseType}</label>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setRollType('under')}
              disabled={rolling}
              className={`py-3 rounded-lg font-bold transition-all ${
                rollType === 'under'
                  ? 'bg-gradient-to-r from-green-500 to-green-600 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              🔽 UNDER
            </button>
            <button
              onClick={() => setRollType('over')}
              disabled={rolling}
              className={`py-3 rounded-lg font-bold transition-all ${
                rollType === 'over'
                  ? 'bg-gradient-to-r from-red-500 to-red-600 text-white'
                  : 'bg-gray-200 text-gray-700'
              }`}
            >
              🔼 OVER
            </button>
          </div>
        </div>

        {/* Target Slider */}
        <div className="mb-4">
          <label className="block text-sm font-semibold mb-2">
            {t.diceTargetNumber}: <span className="text-2xl font-bold text-purple-600">{target}</span>
          </label>
          <input
            type="range"
            min="2"
            max="98"
            value={target}
            onChange={(e) => setTarget(parseInt(e.target.value))}
            disabled={rolling}
            className="w-full h-3 bg-gradient-to-r from-green-400 via-yellow-400 to-red-400 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-600 mt-1">
            <span>2</span>
            <span>50</span>
            <span>98</span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-gradient-to-br from-purple-100 to-purple-200 p-3 rounded-lg">
            <div className="text-xs text-gray-700">{t.diceMultiplier}</div>
            <div className="text-2xl font-bold text-purple-700">{multiplier.toFixed(2)}x</div>
          </div>
          <div className="bg-gradient-to-br from-blue-100 to-blue-200 p-3 rounded-lg">
            <div className="text-xs text-gray-700">{t.diceWinChance}</div>
            <div className="text-2xl font-bold text-blue-700">{winChance}%</div>
          </div>
        </div>

        {/* Roll Button */}
        <button
          onClick={handleRoll}
          disabled={rolling}
          className={`w-full py-4 rounded-lg font-bold text-xl transition-all ${
            rolling
              ? 'bg-gray-400 text-gray-700 cursor-not-allowed'
              : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg'
          }`}
        >
          {rolling ? `🎯 ${t.diceRolling}` : `🎯 ${t.diceRoll}`}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="card bg-red-600/30 border border-red-500 text-white mb-4">
          <div className="font-semibold">❌ {error}</div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div className={`card border-2 mb-4 ${
          result.result === 'win' 
            ? 'bg-green-100/90 border-green-500' 
            : 'bg-red-100/90 border-red-500'
        }`}>
          <div className="text-center">
            <div className="text-4xl mb-3">
              {result.result === 'win' ? '🎉' : '😢'}
            </div>
            <div className={`text-3xl font-bold mb-2 ${
              result.result === 'win' ? 'text-green-700' : 'text-red-700'
            }`}>
              {result.result === 'win' ? t.diceWin : t.diceLoss}
            </div>
            <div className="text-2xl font-bold mb-3">
              {t.diceResult}: <span className={`text-4xl ${
                result.result === 'win' ? 'text-green-600' : 'text-red-600'
              }`}>{result.roll}</span>
            </div>
            <div className="text-sm text-gray-700 mb-2">
              {t.diceTarget}: {result.roll_type === 'under' ? '<' : '>'} {result.target}
            </div>
            {result.result === 'win' && (
              <div className="text-xl font-bold text-green-700">
                +{result.profit.toFixed(4)} TON ({result.multiplier}x)
              </div>
            )}
            {result.result === 'loss' && (
              <div className="text-xl font-bold text-red-700">
                {result.profit.toFixed(4)} TON
              </div>
            )}
          </div>
        </div>
      )}

      {/* How to Play */}
      <div className="card bg-white/80">
        <h3 className="font-bold text-lg mb-3">📖 {t.diceHowToPlay}</h3>
        <ol className="list-decimal list-inside space-y-2 text-sm text-gray-700">
          <li>{t.diceStep1}</li>
          <li>{t.diceStep2}</li>
          <li>{t.diceStep3}</li>
          <li>{t.diceStep4}</li>
          <li>{t.diceStep5}</li>
        </ol>
      </div>
    </div>
  );
};

export default DiceScreen;

